import { Box, Card, CardActions, CardContent, CardMedia } from '@mui/material'

import { ImageDescriptionWrapper } from '@/components/Banner/Banner.styled'
import { LocaleText } from '@/components/LocaleText'
import { RichText } from '@/components/RichText'
import { SanityRichLink } from '@/components/SanityRichLink'
import { resolveSanityImage } from '@/helpers/resolveSanityImage'
import { useMedia } from '@/hooks/useMedia'
import { useTranslations } from '@/hooks/useTranslations'
import { colors } from '@/themes/commerce/colors'

import type * as React from 'react'
import type { UnpackedTextColumnImage } from '@/lib/queries/sanityQueryStrings'

interface ImageBannerProps {
  textColumnImage: UnpackedTextColumnImage
}

export const ImageBanner: React.FC<ImageBannerProps> = ({ textColumnImage }) => {
  const url = resolveSanityImage(textColumnImage?.image)?.auto('format').url() ?? ''
  const { language } = useTranslations()
  const overwriteLinkName = textColumnImage?.ctaLinkName ? textColumnImage?.ctaLinkName[language] : undefined
  const { isMobile } = useMedia()

  const sxTextColor = {
    color: textColumnImage?.textColor ? textColumnImage.textColor.hex : 'inherit',
  }

  const sxBackgroundColor = {
    backgroundColor: textColumnImage?.backgroundColor
      ? textColumnImage.backgroundColor.hex
      : colors.background.component.default,
  }

  return (
    <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
      <Card
        sx={{
          maxWidth: '100%',
          position: 'relative',
          m: 0,
          height: { xs: 'auto', sm: '100%' },
          width: '100%',
          flexDirection: { xs: 'row', sm: 'column' },
          ...(!isMobile && { display: 'flex' }),
        }}
      >
        <CardMedia sx={{ height: 277 }} image={url} title={textColumnImage?.title?.[language]} />
        <CardContent sx={{ ...sxBackgroundColor, ...sxTextColor }}>
          {textColumnImage.subTitle && (
            <LocaleText
              {...textColumnImage.subTitle}
              typographyProps={{
                variant: !isMobile ? 'h5' : 'h6',
                marginTop: '8px',
              }}
            />
          )}
          {textColumnImage.title && (
            <LocaleText
              {...textColumnImage.title}
              typographyProps={{
                variant: !isMobile ? 'h4' : 'h5',
                marginTop: '8px',
              }}
            />
          )}

          {textColumnImage?.description && (
            <ImageDescriptionWrapper>
              <RichText Text={textColumnImage?.description} _type="richText" />
            </ImageDescriptionWrapper>
          )}
          {textColumnImage.ctaLink && (
            <CardActions sx={{ ...sxBackgroundColor, ...sxTextColor, margin: 0, padding: 0, paddingTop: '4px' }}>
              <SanityRichLink sanityLink={textColumnImage.ctaLink} overwriteLinkName={overwriteLinkName} />
            </CardActions>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
